import { useEffect, useMemo } from 'react';
import { useAnalysisData } from '../../contexts/AnalysisDataContext';
import { AnalysisRange } from './useAnalysisRange';
import {
  arrivalsByWeekday,
  departuresByWeekday,
  detectGapNights,
  occupancySeries,
  siteWeekHeatmap,
  totalBlackoutSiteNights,
  weekendVsWeekday,
} from '../../utils/analytics/occupancyMetrics';
import { computeAdr } from '../../utils/analytics/revenueMetrics';
import { enumerateBuckets } from '../../utils/analytics/aggregations';
import { ComparisonMode, comparisonRange, makeDelta } from '../../utils/analytics/comparison';

function averagePct(series: Array<{ pct: number }>): number {
  return series.length
    ? Math.round((series.reduce((acc, p) => acc + p.pct, 0) / series.length) * 10) / 10
    : 0;
}

export function useOccupancyAnalysis(range: AnalysisRange, comparison: ComparisonMode = 'none') {
  const { stays, sites, blackouts, loading, error, ensureRange } = useAnalysisData();
  const prevRange = useMemo(() => comparisonRange(range, comparison), [range, comparison]);

  useEffect(() => {
    ensureRange(prevRange ? prevRange.from : range.from, range.to);
  }, [range.from, range.to, prevRange, ensureRange]);

  return useMemo(() => {
    const { from, to } = range;
    const siteIds = sites.map(s => s.id);
    const series = occupancySeries(stays, siteIds, blackouts, from, to);
    const avgPct = averagePct(series);
    const avgPctDelta = prevRange
      ? makeDelta(
          avgPct,
          averagePct(occupancySeries(stays, siteIds, blackouts, prevRange.from, prevRange.to))
        )
      : undefined;
    const peak = series.reduce(
      (best, p) => (p.pct > best.pct ? p : best),
      { date: '', label: '—', occupied: 0, available: 0, pct: 0 }
    );
    const gaps = detectGapNights(stays, from, to);
    const adr = computeAdr(stays, from, to);
    const weekStarts = enumerateBuckets(from, to, 'week');
    const sortedSites = [...sites].sort((a, b) => a.alias.localeCompare(b.alias, undefined, { numeric: true }));

    return {
      loading,
      error,
      series,
      avgPct,
      avgPctDelta,
      peak,
      split: weekendVsWeekday(series),
      gaps,
      gapLostRevenue: adr !== null ? gaps.length * adr : null,
      blackoutNights: totalBlackoutSiteNights(blackouts, siteIds, from, to),
      arrivals: arrivalsByWeekday(stays, from, to),
      departures: departuresByWeekday(stays, from, to),
      weekStarts,
      heatmapRows: siteWeekHeatmap(
        stays,
        sortedSites.map(s => ({ id: s.id, alias: s.alias })),
        blackouts,
        from,
        to,
        weekStarts
      ),
      siteCount: siteIds.length,
    };
  }, [stays, sites, blackouts, range, prevRange, loading, error]);
}
